import { inject, Inject, Injectable } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, exhaustMap, map } from 'rxjs/operators'
import {
  login,
  loginFailure,
  loginSuccess,
  logout,
  logoutSuccess,
} from './authentication.actions'
import { AuthenticationService } from '@/app/core/service/auth.service'
import { CompanyContextService } from '@/app/services/http/company-context.service'

@Injectable()
export class AuthenticationEffects {


  private actions$ = inject(Actions)
  private companyContextService = inject(CompanyContextService)

  constructor(
    private AuthenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      exhaustMap(({ email, password }) => {
        return this.AuthenticationService.login(email, password).pipe(
          map((user) => {
            if (user) {
              // A previous session on this browser (a different account, or this one
              // ended by token expiry rather than an explicit logout) may have left a
              // stale active-company cookie behind. Never let a fresh login inherit
              // someone else's company selection.
              this.companyContextService.clearActiveCompany()

              // Admin/support aren't staff of any company — send them straight to the
              // company directory instead of the client "My Companies" switcher/dashboard.
              if (this.AuthenticationService.isAdmin || this.AuthenticationService.isSupport) {
                this.router.navigateByUrl('/pages/admin/companies')
              } else {
                const returnUrl =
                  this.route.snapshot.queryParams['returnUrl'] || '/'
                this.router.navigateByUrl(returnUrl)
              }
            }
            return loginSuccess({ user })
          }),
          catchError((error) => of(loginFailure({ error })))
        )
      })
    )
  )

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      exhaustMap(() => {
        this.AuthenticationService.logout()
        this.companyContextService.clearActiveCompany()
        this.router.navigate(['/auth/log-in'])
        return of(logoutSuccess())
      })
    )
  )

}
